import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { KIND, download, errorText, fetchMerchants, fetchZoneSummary, switchToStandard } from './ptlSetupApi'
import { NOT_SERVICEABLE, ROUTES, fmtDate, num } from './constants'
import { Banner, Breadcrumb } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import ZoneListing, { EMPTY_ZONE_FILTERS } from './zone-map/ZoneListing'

const filtersFrom = (params) => Object.fromEntries(Object.keys(EMPTY_ZONE_FILTERS).map((k) => [k, params.get(k) || '']))

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant Zone Map → Overrides → one merchant (plan screen 7, route
 * /zone-map/overrides/:merchantId). The merchant's own pincodes (ptl_merchant_zone_map), each against the standard zone
 * of the same pincode, with KPI cards, the filterable list, download, Upload / History screens and reset to standard.
 */
export default function PtlZoneOverrideMerchant() {
  const { merchantId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [params, setParams] = useSearchParams()
  const [merchants, setMerchants] = useState(null)
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [resetOpen, setResetOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const paramText = params.toString()
  const filters = useMemo(() => filtersFrom(new URLSearchParams(paramText)), [paramText])
  const setFilters = useCallback((next) => {
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v !== '' && v != null)), { replace: true })
  }, [setParams])

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then(setMerchants).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const merchant = useMemo(() => merchants?.find((m) => m.id === merchantId), [merchants, merchantId])
  useEffect(() => {
    if (!merchant) return
    setSummary(null)
    fetchZoneSummary(merchant.id).then(setSummary).catch((e) => setError(errorText(e, 'Could not load the zone map')))
  }, [merchant, refreshKey])

  const base = `${ROUTES.zoneOverrides}/${merchantId}`
  const crumb = <Breadcrumb parent='Merchant overrides' onParent={() => navigate(ROUTES.zoneOverrides)} current={merchant?.name || 'Merchant'} />
  const own = summary ? summary.pincodesMapped + summary.notServiceable : 0
  const latest = summary?.latest

  const doDownload = async () => {
    try {
      const res = await download(KIND.ZONE_MAP, merchant.id)
      saveBlob(res.data, filenameFromResponse(res, `ptl_zone_map_${merchant.id}.csv`))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), { variant: 'error' })
    }
  }

  const reset = async () => {
    setBusy(true)
    try {
      await switchToStandard(KIND.ZONE_MAP, merchant.id)
      enqueueSnackbar(`${merchant.name} is back on the standard zone map`, { variant: 'success' })
      setResetOpen(false)
      setRefreshKey((k) => k + 1)
      load()
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not reset the zone map'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  if (merchants && !merchant) {
    return (
      <PtlPage title='Merchant zone map' breadcrumb={crumb}>
        <EmptyState title='Not a live PTL merchant'
          body='This merchant is not in the PTL merchant list. Turn on PTL for it from Onboarding → Onboard a merchant first, or pick another merchant.'
          action={<Button variant='contained' onClick={() => navigate(ROUTES.zoneOverrides)} sx={SX.cta}>Back to merchant overrides</Button>} />
      </PtlPage>
    )
  }

  const s = summary || {}
  const kpis = [
    { key: 'own', label: 'Own pincodes', value: num(own), sub: own ? 'Use this merchant’s zone' : 'Standard map for every pincode' },
    { key: 'differs', label: 'Zone differs from standard', value: num(s.zoneDiffersFromStandard), tone: s.zoneDiffersFromStandard ? 'warn' : undefined,
      sub: s.notInStandardMap ? `${num(s.notInStandardMap)} not in the standard map` : 'Same pincode, other zone' },
    { key: 'ns', label: 'Not serviceable (9999)', value: num(s.notServiceable), tone: s.notServiceable ? 'danger' : undefined, sub: 'No price, no TAT for this merchant',
      active: filters.zoneId === NOT_SERVICEABLE, onClick: s.notServiceable ? () => setFilters({ ...filters, zoneId: filters.zoneId === NOT_SERVICEABLE ? '' : NOT_SERVICEABLE }) : undefined },
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None',
      sub: latest ? latest.createdBy || '' : 'No override uploaded yet' },
  ]

  return (
    <PtlPage title={merchant ? `Zone map · ${merchant.name}` : 'Merchant zone map'} breadcrumb={crumb}
      description='Pincodes in this map use this merchant’s zone instead of the standard one; every other pincode keeps the standard zone. Only this merchant’s shipments use it.'
      actions={merchant && (
        <>
          <Button variant='outlined' startIcon={<DownloadOutlinedIcon />} disabled={!own} onClick={() => triggerDownload(doDownload, `${merchant.name} zone map`)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            Download own pincodes
          </Button>
          <Button variant='outlined' startIcon={<HistoryOutlinedIcon />} onClick={() => navigate(`${base}/history`)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>History</Button>
          <Button variant='contained' startIcon={<UploadFileOutlinedIcon />} disabled={!summary} onClick={() => navigate(`${base}/upload`)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
            {own ? 'Upload override' : 'Add override'}
          </Button>
        </>
      )}
      kpis={kpis} kpisLoading={!summary && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {summary && !own ? (
        <EmptyState title={`${merchant.name} uses the standard zone map`}
          body='Upload an override file only if this merchant’s contract maps some pincodes to other zones. Every pincode not in the file keeps the standard zone.'
          action={<Button variant='contained' startIcon={<UploadFileOutlinedIcon />} onClick={() => navigate(`${base}/upload`)} sx={SX.cta}>Add override</Button>} />
      ) : merchant && (
        <>
          <ZoneListing merchantId={merchant.id} zones={summary?.zones?.filter((z) => z.standard)} filters={filters} onFilters={setFilters} refreshKey={refreshKey} />
          <Typography sx={{ ...TYPE.rowHelp, mt: 1.5, color: C.inkMuted }}>
            Standard zone shows the standard map’s zone for the same pincode; a different value is what this override changes.{' '}
            <Button size='small' onClick={() => setResetOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, color: C.danger, verticalAlign: 'baseline' }}>Reset to standard</Button>
          </Typography>
        </>
      )}

      {PolicyDialog}
      <Dialog open={resetOpen} onClose={() => !busy && setResetOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Put {merchant?.name} back on the standard zone map?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            Its {num(own)} own pincode{own === 1 ? '' : 's'} switch off and every pincode uses the standard zone for its shipments priced from now on.
            This changes the merchant's pricing at once and cannot be rolled back: to bring the own map back, upload it again. The change is kept in its History.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' color='error' loading={busy} onClick={reset} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>Reset to standard</LoadingButton>
        </DialogActions>
      </Dialog>
    </PtlPage>
  )
}
